import { getUnitSourceCategory } from '../../../functions/shared/units/getUnitSourceCategory'
import { getUnitReviewActivityEvents, normalizeUnitReview as normalizeUnitReviewSections, type UnitActivityEvent } from '../../../functions/shared/units/unitReviewActivity'
import { formatUnitReviewReasonLabels, getUnitReviewReasonLabel, getUnitReviewSelectOptions, UNIT_REVIEW_OPTIONS, type UnitReviewCategory, type UnitReviewOption, type UnitReviewReason } from '../../../functions/shared/units/unitReviewOptions'
import { isBsoRentalOrSaleUnit, isVisibleInAgentCatalog } from '../../../functions/shared/units/unitVisibility'

export { formatUnitReviewReasonLabels, getUnitReviewReasonLabel, getUnitReviewSelectOptions, UNIT_REVIEW_OPTIONS, type UnitReviewCategory, type UnitReviewOption, type UnitReviewReason } from '../../../functions/shared/units/unitReviewOptions'

const DUBAI_TIME_ZONE = 'Asia/Dubai'
const TERMINAL_OFFER_STATUSES = new Set(['Canceled', 'Completed', 'Rejected', 'Resubmit required'])
const DEFAULT_UNIT_IMAGE = '/assets/media/placeholders/image_unavailable.png'
const CONDITION_STATUSES = ['Vacant', 'Occupied'] as const
const ACCESS_STATUSES = ['Keys in BSO office', 'Digital lock', 'Building Management', 'Door open', 'Door close', 'Door closed', 'Special Request'] as const

type UnitMarketStatus = 'Active' | 'Hidden' | 'Archived' | 'Off Market'
export interface BsoUnitReview {
    temporaryReasons: UnitReviewReason[]
    permanentReasons: UnitReviewReason[]
    updatedAt: string
    updatedBy: number | string | null
}


export interface BsoUnitStatisticEvent {
    unitId: number
    unitReference: string
    propertyName: string
    offerId: number
    offerRoute: string | null
    processStartDate: string | null
    processEndDate: string | null
    isProcessEndDateInferred: boolean
    daysPublished: number | null
    trigger: 'Completed Offer'
}

export interface BsoStatisticsUnitDetailItem {
    unitId: number
    isDevUnit: boolean
    unitReference: string
    headline: string
    propertyName: string
    areaName: string
    unitNumber: string | null
    erpUnitId: string | null
    priceText: string | null
    listingType: 'Sale' | 'Rent' | null
    unitType: string | null
    unitCategory: string | null
    sizeArea: number | null
    roomsLabel: string | null
    marketStatus: UnitMarketStatus | null
    conditionStatus: 'Vacant' | 'Occupied' | null
    marketingUse: string | null
    statuses: string[]
    accessStatuses: string[]
    imageUrl: string
    unitRoute: string | null
    occupiedUntil: string | null
    catalogDate: string | null
    catalogDateSource: 'published-date' | 'created-date' | 'unavailable'
    endDate: string | null
    initialPrice: number | null
    currentPrice: number | null
    priceDrop: number | null
    initialPriceSource: 'current-price-fallback' | 'unavailable'
    offerId: number | null
    offerRoute: string | null
    processStartDate?: string | null
    processEndDate?: string | null
    endReason?: string | null
    isProcessEndDateInferred?: boolean
    daysPublished: number | null
    unitReview: BsoUnitReview | null
}

export interface BsoUnitAnalyticsRow {
    unitId: number
    propertyId: number | null
    isDevUnit: boolean
    headline: string
    propertyName: string
    areaName: string
    unitNumber: string | null
    erpUnitId: string | null
    unitReference: string
    createdAt: string | null
    imageUrl: string
    listingType: 'Sale' | 'Rent' | null
    unitType: string | null
    unitCategory: string | null
    sizeArea: number | null
    roomsLabel: string | null
    marketStatus: UnitMarketStatus | null
    conditionStatus: 'Vacant' | 'Occupied' | null
    occupiedUntil: string | null
    accessStatuses: string[]
    endDate: string | null
    endReason: string | null
    offerId: number | null
    offerRoute: string | null
    daysPublished: number | null
    catalogDate: string | null
    catalogDateSource: 'published-date' | 'created-date' | 'unavailable'
    initialPrice: number | null
    currentPrice: number | null
    priceDrop: number | null
    initialPriceSource: 'current-price-fallback' | 'unavailable'
    adsCount: number
    viewingsCount: number
    offersCount: number
    adsSummary: EntityBreakdownSummary
    viewingsSummary: EntityBreakdownSummary
    offersSummary: EntityBreakdownSummary
    unitReview: BsoUnitReview | null
    unitActivity: UnitActivityEvent[]
}

export type ActivityBucketKey = 'in_process' | 'negotiation' | 'completed' | 'rejected' | 'other'

export interface ActivityBucket {
    key: ActivityBucketKey
    label: string
    count: number
    statuses: string[]
}

export interface EntityBreakdownSummary {
    total: number
    countBreakdown?: EntityTestCountBreakdown
    manualByBsoCount?: number
    buckets: ActivityBucket[]
}

export interface EntityTestCountBreakdown {
    real: number
    test: number
    total: number
}

export interface BsoActivitySummary {
    advertisements: EntityBreakdownSummary
    viewings: EntityBreakdownSummary
    offers: EntityBreakdownSummary
}

export interface ActiveOfferPipelineItem {
    unitId: number
    unitReference: string
    propertyName: string
    offerId: number
    offerRoute: string | null
    unitCoverImage: string
    status: string
    processStartDate: string | null
    processEndDate: string | null
    agentName: string | null
    agentCompany: string | null
    agentBirthYear: string | null
    agentRoute: string | null
}

export interface StatisticPeriod {
    id: 'this-month' | 'previous-month' | 'two-months-ago' | 'this-quarter' | 'previous-quarter' | 'this-year' | 'previous-year' | 'last-12-months'
    label: string
    count: number
    averageDaysPublished: number | null
    events: BsoUnitStatisticEvent[]
}

export interface BsoUnitStatistics {
    totalUnits: number
    totalRentalUnits: number
    totalSaleUnits: number
    activeRentals: number
    activeSales: number
    activeTotal: number
    soldUnits: number
    soldRentUnits: number
    soldSaleUnits: number
    soldPeriods: StatisticPeriod[]
    soldThisMonth: number
    soldNeedsDataReview: number
    activeOfferPipeline: ActiveOfferPipelineItem[]
    totalUnitDetails: BsoStatisticsUnitDetailItem[]
    activeUnitDetails: BsoStatisticsUnitDetailItem[]
    soldUnitDetails: BsoStatisticsUnitDetailItem[]
}

interface DubaiDateParts {
    year: number
    month: number
    day: number
}

const getAttributes = (item: any): any => item?.attributes ?? item ?? {}

const getEntityIsDevValue = (item: any): unknown => {
    const attributes = item?.attributes
    if (attributes != null && typeof attributes === 'object') {
        if ('IsDev' in attributes) return attributes.IsDev
    }
    if (item != null && typeof item === 'object') {
        if ('IsDev' in item) return item.IsDev
    }
    return undefined
}

export const isDevEntity = (item: any): boolean => getEntityIsDevValue(item) === true

export const splitRealTestCounts = (items: any[], fallbackIsDev: boolean): EntityTestCountBreakdown =>
    items.reduce<EntityTestCountBreakdown>((counts, item) => {
        const isDevValue = getEntityIsDevValue(item)
        const isTest = isDevValue === true || (isDevValue == null && fallbackIsDev)
        if (isTest) {
            counts.test += 1
        } else {
            counts.real += 1
        }
        counts.total += 1
        return counts
    }, { real: 0, test: 0, total: 0 })

export const isManualBsoAction = (entity: any): boolean => {
    const manualUserId = Number(process.env.REACT_APP_BSO_MANUAL_ACTION_USER_ID)
    if (!Number.isFinite(manualUserId)) return false

    const attributes = getAttributes(entity)
    const entityUserId = Number(
        attributes?.User?.data?.id ??
        attributes?.User?.id ??
        attributes?.UserID ??
        attributes?.userID ??
        attributes?.userId ??
        attributes?.User ??
        entity?.User?.data?.id ??
        entity?.User?.id ??
        entity?.UserID ??
        entity?.userID ??
        entity?.userId ??
        entity?.User
    )

    return Number.isFinite(entityUserId) && entityUserId === manualUserId
}

const parseJsonObject = (value: unknown): Record<string, unknown> | null => {
    if (value == null) return null
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value)
            return parsed != null && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : null
        } catch {
            return null
        }
    }
    return typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
}

export const normalizeUnitReview = (value: unknown): BsoUnitReview | null => {
    const review = parseJsonObject(normalizeUnitReviewSections(value).review)
    if (review == null) return null

    const reasonsFor = (category: UnitReviewCategory, key: 'temporaryReasons' | 'permanentReasons'): UnitReviewReason[] => {
        const value = review[key]
        return Array.isArray(value) ? value.reduce<UnitReviewReason[]>((result, item) => {
            if (item == null || typeof item !== 'object' || Array.isArray(item)) return result
            const reason = item as Record<string, unknown>
            const reasonValue = typeof reason.value === 'string' ? reason.value.trim() : ''
            if (reasonValue === '' || result.some((entry) => entry.value === reasonValue)) return result
            return [...result, { value: reasonValue, label: typeof reason.label === 'string' ? reason.label : getUnitReviewReasonLabel(category, reasonValue) }]
        }, []) : []
    }
    const temporaryReasons = reasonsFor('temporary', 'temporaryReasons')
    const permanentReasons = reasonsFor('permanent', 'permanentReasons')
    if (temporaryReasons.length === 0 && permanentReasons.length === 0) return null

    return {
        temporaryReasons,
        permanentReasons,
        updatedAt: typeof review.updatedAt === 'string' ? review.updatedAt : '',
        updatedBy: typeof review.updatedBy === 'number' || typeof review.updatedBy === 'string' ? review.updatedBy : null
    }
}

const toDateOnly = (value: unknown): string | null => {
    if (typeof value !== 'string' || value.trim() === '') return null
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : value
}

const resolveOfferProcessStartDate = (offerAttributes: any): string | null =>
    toDateOnly(offerAttributes?.ProcessStartDate)

const resolveOfferProcessEndDate = (offerAttributes: any): string | null =>
    toDateOnly(offerAttributes?.ProcessEndDate)

const resolveOfferEffectiveProcessEndDate = (offerAttributes: any): { date: string | null, inferredFromStartDate: boolean } => {
    const processEndDate = resolveOfferProcessEndDate(offerAttributes)
    if (processEndDate != null) {
        return {
            date: processEndDate,
            inferredFromStartDate: false
        }
    }

    const processStartDate = resolveOfferProcessStartDate(offerAttributes)
    if (processStartDate != null) {
        return {
            date: processStartDate,
            inferredFromStartDate: true
        }
    }

    return {
        date: null,
        inferredFromStartDate: false
    }
}

const getDubaiDateParts = (value: Date | string): DubaiDateParts | null => {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return null

    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: DUBAI_TIME_ZONE,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    }).formatToParts(date)

    const year = Number(parts.find((part) => part.type === 'year')?.value)
    const month = Number(parts.find((part) => part.type === 'month')?.value)
    const day = Number(parts.find((part) => part.type === 'day')?.value)
    return Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day) ? { year, month, day } : null
}

const shiftMonth = ({ year, month, day }: DubaiDateParts, offset: number): DubaiDateParts => {
    const zeroBasedMonth = month - 1 + offset
    return {
        year: year + Math.floor(zeroBasedMonth / 12),
        month: ((zeroBasedMonth % 12) + 12) % 12 + 1,
        day
    }
}

const getQuarter = ({ year, month }: DubaiDateParts): { year: number, quarter: number } => ({
    year,
    quarter: Math.floor((month - 1) / 3) + 1
})

const isSameMonth = (left: DubaiDateParts, right: DubaiDateParts): boolean =>
    left.year === right.year && left.month === right.month

const isSameQuarter = (left: DubaiDateParts, right: DubaiDateParts): boolean => {
    const leftQuarter = getQuarter(left)
    const rightQuarter = getQuarter(right)
    return leftQuarter.year === rightQuarter.year && leftQuarter.quarter === rightQuarter.quarter
}

const isSameYear = (left: DubaiDateParts, right: DubaiDateParts): boolean =>
    left.year === right.year

const isPreviousYear = (left: DubaiDateParts, right: DubaiDateParts): boolean =>
    left.year === right.year - 1

const getDubaiDateSerial = (value: Date | string): number | null => {
    const parts = getDubaiDateParts(value)
    return parts == null ? null : Date.UTC(parts.year, parts.month - 1, parts.day)
}

const isWithinLast12Months = (value: Date | string, now: Date): boolean => {
    const eventSerial = getDubaiDateSerial(value)
    const currentSerial = getDubaiDateSerial(now)
    const currentParts = getDubaiDateParts(now)
    if (eventSerial == null || currentSerial == null || currentParts == null) return false

    const start = new Date(Date.UTC(currentParts.year, currentParts.month - 1, currentParts.day))
    start.setUTCFullYear(start.getUTCFullYear() - 1)
    return eventSerial >= start.getTime() && eventSerial <= currentSerial
}

const calculateDaysBetween = (startDate: string | null, endDate: string | null): number | null => {
    if (startDate == null || endDate == null) return null
    const start = new Date(startDate)
    const end = new Date(endDate)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
    return Math.max(0, Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)))
}

const resolveUnitStartDate = (unit: any): string | null => {
    const attributes = getAttributes(unit)
    return attributes?.CreateDate ?? attributes?.createdAt ?? null
}

const resolveUnitCatalogDate = (unit: any): { date: string | null, source: BsoUnitAnalyticsRow['catalogDateSource'] } => {
    const attributes = getAttributes(unit)
    const publishedDate = toDateOnly(attributes?.PublishedDate)
    if (publishedDate != null) return { date: publishedDate, source: 'published-date' }

    // Fallback only: CreateDate/createdAt is the unit/listing creation timestamp, not guaranteed publication date.
    const createdDate = toDateOnly(resolveUnitStartDate(unit))
    return createdDate != null
        ? { date: createdDate, source: 'created-date' }
        : { date: null, source: 'unavailable' }
}

const toFiniteNumberOrNull = (value: unknown): number | null => {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
}

const toPositiveNumberOrNull = (value: unknown): number | null => {
    const numeric = toFiniteNumberOrNull(value)
    return numeric != null && numeric > 0 ? numeric : null
}

const getUnitReference = (unit: any): string => {
    const attributes = getAttributes(unit)
    return String(attributes?.RefID ?? attributes?.Number ?? `Unit #${String(unit?.id ?? '')}`)
}

const getPropertyName = (unit: any): string => {
    const attributes = getAttributes(unit)
    return String(attributes?.Property?.data?.attributes?.Name ?? attributes?.Marketing?.Headline ?? '-')
}

const getUnitCoverImage = (unit: any): string => {
    const attributes = getAttributes(unit)
    const image = attributes?.Images?.[0]
    return typeof image === 'string' && image.trim() !== '' ? image : DEFAULT_UNIT_IMAGE
}

const resolveListingType = (use: unknown): BsoUnitAnalyticsRow['listingType'] => {
    if (use === 'For sale') return 'Sale'
    if (use === 'For rent') return 'Rent'
    return null
}

const toNonEmptyStringOrNull = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() !== '' ? value : null

const toUnitMetaStringOrNull = (value: unknown): string | null => {
    if (typeof value === 'string') return value.trim() !== '' ? value : null
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
    if (value == null || typeof value !== 'object') return null

    const record = value as any
    const nestedAttributes = record?.data?.attributes ?? record?.attributes
    const candidates = [
        record?.Name,
        record?.Title,
        record?.Value,
        record?.value,
        record?.label,
        nestedAttributes?.Name,
        nestedAttributes?.Title,
        nestedAttributes?.Value,
        nestedAttributes?.value,
        nestedAttributes?.label,
        nestedAttributes?.Category,
        nestedAttributes?.UnitCategory
    ]

    for (const candidate of candidates) {
        const stringValue = toNonEmptyStringOrNull(candidate)
        if (stringValue != null) return stringValue
    }

    return null
}

const normalizeComparableMeta = (value: string): string =>
    value.trim().toLowerCase()

const resolveUnitCategory = (attributes: any, unitType: string | null): string | null => {
    const categoryCandidates = [
        attributes?.UnitCategory,
        // Unit create/update flow stores the selected UnitCategory into Category before API submit.
        attributes?.Category,
        attributes?.PropertyInfo?.UnitCategory,
        attributes?.PropertyInfo?.Category,
        attributes?.PropertyInfo?.data?.attributes?.UnitCategory,
        attributes?.PropertyInfo?.data?.attributes?.Category
    ]

    for (const candidate of categoryCandidates) {
        const category = toUnitMetaStringOrNull(candidate)
        if (category == null) continue
        if (unitType != null && normalizeComparableMeta(category) === normalizeComparableMeta(unitType)) continue
        return category
    }

    return null
}

const resolveRoomsLabel = (attributes: any): string | null => {
    const bedrooms = toPositiveNumberOrNull(attributes?.Bedrooms)
    if (bedrooms != null) return `${String(bedrooms)} BR`

    const rooms = toPositiveNumberOrNull(attributes?.Rooms)
    if (rooms != null) return `${String(rooms)} rooms`

    return null
}

const buildUnitModalRoute = (unitId: number): string | null =>
    Number.isFinite(unitId) ? `/landlord/modalView/unit_modal/${String(unitId)}/not/catalog` : null

const formatUnitPrice = (value: unknown): string | null => {
    const price = toFiniteNumberOrNull(value)
    return price != null ? `AED ${price.toLocaleString('en-US')}` : null
}

const buildUnitDetailItem = (unit: any): BsoStatisticsUnitDetailItem => {
    const attributes = getAttributes(unit)
    const property = attributes?.Property?.data?.attributes
    const unitId = Number(unit?.id)
    const statuses: string[] = Array.isArray(attributes?.Statuses) ? attributes.Statuses : []
    const catalogDate = resolveUnitCatalogDate(unit)
    const currentPrice = toFiniteNumberOrNull(attributes?.Marketing?.Price)
    const initialPrice = currentPrice
    const marketStatus = resolveMarketStatus(unit)
    const unitType = toNonEmptyStringOrNull(attributes?.Type)
    const activeDaysPublished = marketStatus === 'Active'
        ? calculateDaysBetween(catalogDate.date, new Date().toISOString())
        : undefined
    return {
        unitId,
        isDevUnit: isDevEntity(unit),
        unitReference: getUnitReference(unit),
        headline: String(attributes?.Marketing?.Headline ?? property?.Name ?? getUnitReference(unit)),
        propertyName: String(property?.Name ?? '-'),
        areaName: String(property?.Area ?? property?.Community ?? '-'),
        unitNumber: attributes?.Number != null ? String(attributes.Number) : null,
        erpUnitId: toNonEmptyStringOrNull(attributes?.ERPUnitID),
        priceText: formatUnitPrice(attributes?.Marketing?.Price),
        listingType: resolveListingType(attributes?.Marketing?.Use),
        unitType,
        unitCategory: resolveUnitCategory(attributes, unitType),
        sizeArea: toPositiveNumberOrNull(attributes?.SizeArea),
        roomsLabel: resolveRoomsLabel(attributes),
        marketStatus,
        conditionStatus: resolveConditionStatus(statuses),
        marketingUse: attributes?.Marketing?.Use ?? null,
        statuses,
        accessStatuses: resolveAccessStatuses(statuses),
        imageUrl: getUnitCoverImage(unit),
        unitRoute: buildUnitModalRoute(unitId),
        occupiedUntil: resolveOccupiedUntil(attributes, statuses),
        catalogDate: catalogDate.date,
        catalogDateSource: catalogDate.source,
        endDate: null,
        initialPrice,
        currentPrice,
        priceDrop: null,
        initialPriceSource: initialPrice != null ? 'current-price-fallback' : 'unavailable',
        offerId: null,
        offerRoute: null,
        daysPublished: activeDaysPublished ?? null,
        unitReview: normalizeUnitReview(attributes?.UnitReview)
    }
}

const buildOfferModalRoute = (unitId: number, offerId: number, offerAttributes: any): string | null => {
    const offerType = offerAttributes?.Type?.[0]?.__component
    if (!Number.isFinite(unitId) || !Number.isFinite(offerId) || typeof offerType !== 'string' || offerType.trim() === '') {
        return null
    }
    return `/landlord/modalView/magic_offer_modal/${String(unitId)}/${String(offerId)}/catalog?type=${encodeURIComponent(offerType)}`
}

const buildAgentRoute = (agentId: number): string | null =>
    Number.isFinite(agentId) ? `/landlord/profile/${String(agentId)}` : null

const resolveAgentBirthYear = (agent: any): string | null => {
    const dateOfBirth = agent?.ContactInfo?.DateOfBirth ?? agent?.Type?.[0]?.DateOfBirth
    if (typeof dateOfBirth !== 'string' || dateOfBirth.trim() === '') return null
    const date = new Date(dateOfBirth)
    return Number.isNaN(date.getTime()) ? null : String(date.getFullYear())
}

const resolveAgentName = (agent: any): string | null => {
    const name = [agent?.ContactInfo?.FirstName, agent?.ContactInfo?.FamilyName]
        .filter((value) => typeof value === 'string' && value.trim() !== '')
        .join(' ')
        .trim()
    return name === '' ? null : name
}

const isActiveBsoUnit = (unit: any): boolean => {
    return isBsoRentalOrSaleUnit(unit) && isVisibleInAgentCatalog(unit)
}

const isCategorizedBsoUnit = (unit: any): boolean => {
    return isBsoRentalOrSaleUnit(unit)
}

export const getRelatedUnitId = (item: any): number | null => {
    const attributes = getAttributes(item)
    const candidates = [
        attributes?.Unit?.data?.id,
        attributes?.Unit?.id,
        item?.Unit?.data?.id,
        item?.Unit?.id,
        item?.unitId,
        item?.unitID,
        item?.UnitID
    ]

    for (const candidate of candidates) {
        const numeric = Number(candidate)
        if (Number.isFinite(numeric)) return numeric
    }

    return null
}

const getRelationArray = (unit: any, relationName: 'Advertises' | 'Viewings' | 'Offers'): any[] => {
    const attributes = getAttributes(unit)
    const relation = attributes?.[relationName]
    const values = relation?.data ?? relation
    return Array.isArray(values) ? values : []
}

const dedupeRelatedItemsById = (items: any[]): any[] => {
    const indexById = new Map<number, number>()
    const uniqueItems: any[] = []

    items.forEach((item) => {
        const itemId = Number(item?.id)
        if (!Number.isFinite(itemId)) {
            uniqueItems.push(item)
            return
        }

        const existingIndex = indexById.get(itemId)
        if (existingIndex == null) {
            indexById.set(itemId, uniqueItems.length)
            uniqueItems.push(item)
            return
        }

        uniqueItems[existingIndex] = item
    })

    return uniqueItems
}

export const getRelatedItems = (unit: any, relatedItems: any[], relationName: 'Advertises' | 'Viewings' | 'Offers'): any[] => {
    const unitId = Number(unit?.id)
    if (!Number.isFinite(unitId)) return []

    const directItems = dedupeRelatedItemsById(relatedItems.filter((item) => getRelatedUnitId(item) === unitId))
    if (directItems.length > 0) return directItems

    return dedupeRelatedItemsById(getRelationArray(unit, relationName))
}

export interface ActivityBucketDefinition {
    key: ActivityBucketKey
    label: string
    matches: (status: string, item: any, now: Date) => boolean
}

const OFFER_IN_PROCESS_STATUSES = new Set([
    'In-process',
    'Sent to landlord',
    'Pending BSO admin confirmation'
])

const OFFER_NEGOTIATION_STATUSES = new Set([
    'Negotiated by BSO',
    'Negotiated by landlord',
    'Negotiated by agent',
    'Negotiated by other agent',
    'Negotiation confirmed by agent'
])

const OFFER_COMPLETED_STATUSES = new Set([
    'Approved',
    'Confirmed',
    'Completed',
    'Waiting for slip approval',
    'Slip approved',
    'Waiting booking form send',
    'Waiting booking form sign',
    'F Form requested',
    'Manual contract requested',
    'Waiting draft contract send',
    'Waiting draft contract approve',
    'Draft contract approved',
    'F Form sent',
    'Manual contract sent',
    'F Form signed',
    'Manual contract signed',
    'Downpayment',
    'Signing F form',
    'Signing other documents',
    'Obtaining NOC',
    'Transfer process',
    'Waiting for updates'
])

const OFFER_REJECTED_STATUSES = new Set([
    'Rejected',
    'Canceled',
    'Canceled by BSO',
    'Canceled by yourself',
    'Canceled by agent',
    'Canceled by other agent',
    'Canceled by landlord',
    'Slip rejected'
])

const VIEWING_IN_PROCESS_STATUSES = new Set([
    'In-process',
    'Rescheduled by agent',
    'Rescheduled by other agent',
    'Rescheduled by BSO',
    'Rescheduled by landlord',
    'Approved',
    'Waiting for feedback'
])

const VIEWING_COMPLETED_STATUSES = new Set([
    'Completed',
    'Waiting for complain'
])

const VIEWING_REJECTED_STATUSES = new Set([
    'Rejected',
    'Canceled',
    'Canceled by BSO',
    'Canceled by agent',
    'Canceled by other agent'
])

const ADVERTISE_IN_PROCESS_STATUSES = new Set([
    'In-process',
    'Docs received',
    'A Form added',
    'Links added',
    'Confirmed',
    'Ads removing rejected',
    'Advertisement started'
])

const ADVERTISE_COMPLETED_STATUSES = new Set([
    'Completed',
    'Advertisement closed',
    'Ads removed'
])

const ADVERTISE_REJECTED_STATUSES = new Set([
    'Canceled',
    'A Form rejected',
    'Links rejected'
])

export const OFFER_BUCKET_DEFINITIONS: ActivityBucketDefinition[] = [
    {
        key: 'in_process',
        label: 'In process',
        matches: (status) => OFFER_IN_PROCESS_STATUSES.has(status)
    },
    {
        key: 'negotiation',
        label: 'Negotiation',
        matches: (status) => OFFER_NEGOTIATION_STATUSES.has(status)
    },
    {
        key: 'completed',
        label: 'Completed / Approved',
        matches: (status) => OFFER_COMPLETED_STATUSES.has(status)
    },
    {
        key: 'rejected',
        label: 'Rejected / Cancelled',
        matches: (status) => OFFER_REJECTED_STATUSES.has(status)
    },
    {
        key: 'other',
        label: 'Other',
        matches: () => false
    }
]

export const VIEWING_BUCKET_DEFINITIONS: ActivityBucketDefinition[] = [
    {
        key: 'in_process',
        label: 'Scheduled / In process',
        matches: (status) => VIEWING_IN_PROCESS_STATUSES.has(status)
    },
    {
        key: 'completed',
        label: 'Completed / Done',
        matches: (status) => VIEWING_COMPLETED_STATUSES.has(status)
    },
    {
        key: 'rejected',
        label: 'Cancelled / Rejected',
        matches: (status) => VIEWING_REJECTED_STATUSES.has(status)
    },
    {
        key: 'other',
        label: 'Other',
        matches: () => false
    }
]

export const ADVERTISE_BUCKET_DEFINITIONS: ActivityBucketDefinition[] = [
    {
        key: 'in_process',
        label: 'Active / In process',
        matches: (status, item, now) => {
            if (ADVERTISE_IN_PROCESS_STATUSES.has(status)) return true
            if (status !== 'Links approved') return false

            const attributes = getAttributes(item)
            const start = typeof attributes?.StartDate === 'string' ? new Date(attributes.StartDate) : null
            const end = typeof attributes?.EndDate === 'string' ? new Date(attributes.EndDate) : null
            return start != null &&
                end != null &&
                !Number.isNaN(start.getTime()) &&
                !Number.isNaN(end.getTime()) &&
                start.getTime() < now.getTime() &&
                now.getTime() < end.getTime()
        }
    },
    {
        key: 'completed',
        label: 'Closed / Completed',
        matches: (status) => ADVERTISE_COMPLETED_STATUSES.has(status)
    },
    {
        key: 'rejected',
        label: 'Rejected / Cancelled',
        matches: (status) => ADVERTISE_REJECTED_STATUSES.has(status)
    },
    {
        key: 'other',
        label: 'Other',
        matches: () => false
    }
]

export const normalizeStatuses = (item: any): string[] => {
    const attributes = getAttributes(item)
    return Array.isArray(attributes?.Statuses)
        ? attributes.Statuses.filter((status: unknown): status is string => typeof status === 'string' && status.trim() !== '')
        : []
}

const createEmptyBreakdownSummary = (definitions: ActivityBucketDefinition[]): EntityBreakdownSummary => ({
    total: 0,
    buckets: definitions.map((definition) => ({
        key: definition.key,
        label: definition.label,
        count: 0,
        statuses: []
    }))
})

const uniqSorted = (values: string[]): string[] =>
    Array.from(new Set(values)).sort((left, right) => left.localeCompare(right))

export const summarizeItemsByBuckets = (items: any[], definitions: ActivityBucketDefinition[], now: Date): EntityBreakdownSummary => {
    const bucketMap = new Map<ActivityBucketKey, { key: ActivityBucketKey, label: string, count: number, statuses: Set<string> }>(
        definitions.map((definition) => [
            definition.key,
            {
                key: definition.key,
                label: definition.label,
                count: 0,
                statuses: new Set<string>()
            }
        ])
    )
    const otherBucket = bucketMap.get('other') ?? null

    items.forEach((item) => {
        const statuses = normalizeStatuses(item)
        const matchedDefinition = definitions.find((definition) =>
            definition.key !== 'other' && statuses.some((status) => definition.matches(status, item, now))
        )
        const bucket = matchedDefinition == null
            ? otherBucket
            : bucketMap.get(matchedDefinition.key)

        if (bucket == null) return
        bucket.count += 1

        const relevantStatuses = matchedDefinition == null
            ? statuses
            : statuses.filter((status) => matchedDefinition.matches(status, item, now))

        relevantStatuses.forEach((status) => bucket.statuses.add(status))
    })

    return {
        total: items.length,
        buckets: definitions.map((definition) => {
            const bucket = bucketMap.get(definition.key)
            return {
                key: definition.key,
                label: definition.label,
                count: bucket?.count ?? 0,
                statuses: uniqSorted(Array.from(bucket?.statuses ?? []))
            }
        })
    }
}

export const buildOfferSummary = (items: any[], now: Date): EntityBreakdownSummary =>
    summarizeItemsByBuckets(items, OFFER_BUCKET_DEFINITIONS, now)

export const buildViewingSummary = (items: any[], now: Date): EntityBreakdownSummary =>
    summarizeItemsByBuckets(items, VIEWING_BUCKET_DEFINITIONS, now)

export const buildAdvertiseSummary = (items: any[], now: Date): EntityBreakdownSummary =>
    summarizeItemsByBuckets(items, ADVERTISE_BUCKET_DEFINITIONS, now)

const mergeBreakdownSummaries = (summaries: EntityBreakdownSummary[], definitions: ActivityBucketDefinition[]): EntityBreakdownSummary => {
    const merged = createEmptyBreakdownSummary(definitions)
    merged.total = summaries.reduce((total, summary) => total + summary.total, 0)
    merged.manualByBsoCount = summaries.reduce((total, summary) => total + (summary.manualByBsoCount ?? 0), 0)
    const mergedCountBreakdown = summaries.reduce<EntityTestCountBreakdown>((counts, summary) => {
        const breakdown = summary.countBreakdown
        if (breakdown == null) {
            counts.real += summary.total
            counts.total += summary.total
            return counts
        }
        counts.real += breakdown.real
        counts.test += breakdown.test
        counts.total += breakdown.total
        return counts
    }, { real: 0, test: 0, total: 0 })
    merged.countBreakdown = mergedCountBreakdown

    summaries.forEach((summary) => {
        summary.buckets.forEach((bucket) => {
            const targetBucket = merged.buckets.find((candidate) => candidate.key === bucket.key)
            if (targetBucket == null) return
            targetBucket.count += bucket.count
            targetBucket.statuses = uniqSorted([...targetBucket.statuses, ...bucket.statuses])
        })
    })

    return merged
}

const resolveConditionStatus = (statuses: string[]): BsoUnitAnalyticsRow['conditionStatus'] =>
    CONDITION_STATUSES.find((status) => statuses.includes(status)) ?? null

const resolveOccupiedUntil = (attributes: any, statuses: string[]): string | null =>
    statuses.includes('Occupied') ? toDateOnly(attributes?.OccupiedUntil) : null

const resolveAccessStatuses = (statuses: string[]): string[] =>
    ACCESS_STATUSES.filter((status) => statuses.includes(status))

const resolveMarketStatus = (unit: any): UnitMarketStatus | null => {
    const attributes = getAttributes(unit)
    const statuses: string[] = Array.isArray(attributes?.Statuses) ? attributes.Statuses : []

    if (statuses.includes('Archived')) return 'Archived'
    if (statuses.includes('Drafted')) return 'Hidden'
    if (statuses.includes('Sold')) return 'Off Market'
    if (isActiveBsoUnit(unit)) return 'Active'
    return null
}

const resolveCompletedOfferEndInfo = (unit: any, offers: any[]): { endDate: string | null, endReason: string | null, offerId: number | null, offerRoute: string | null } => {
    const unitId = Number(unit?.id)
    if (!Number.isFinite(unitId)) return { endDate: null, endReason: null, offerId: null, offerRoute: null }

    const completedOffers = offers
        .filter((offer) => {
            const offerAttributes = getAttributes(offer)
            const statuses: string[] = Array.isArray(offerAttributes?.Statuses) ? offerAttributes.Statuses : []
            return getRelatedUnitId(offer) === unitId && statuses.includes('Completed')
        })
        .map((offer) => {
            const offerAttributes = getAttributes(offer)
            const effectiveEndDate = resolveOfferEffectiveProcessEndDate(offerAttributes)
            return {
                offerId: Number(offer?.id),
                endDate: effectiveEndDate.date,
                offerRoute: buildOfferModalRoute(unitId, Number(offer?.id), offerAttributes)
            }
        })
        .filter((offer): offer is { offerId: number, endDate: string, offerRoute: string | null } => offer.endDate != null)
        .sort((left, right) => new Date(right.endDate).getTime() - new Date(left.endDate).getTime())

    const latestCompletedOffer = completedOffers[0]
    return latestCompletedOffer == null
        ? { endDate: null, endReason: null, offerId: null, offerRoute: null }
        : {
            endDate: latestCompletedOffer.endDate,
            endReason: Number.isFinite(latestCompletedOffer.offerId) ? `Completed offer #${String(latestCompletedOffer.offerId)}` : 'Completed offer',
            offerId: Number.isFinite(latestCompletedOffer.offerId) ? latestCompletedOffer.offerId : null,
            offerRoute: latestCompletedOffer.offerRoute
        }
}

export const buildBsoUnitAnalyticsRows = (units: any[], offers: any[], viewings: any[], advertises: any[], now: Date = new Date()): BsoUnitAnalyticsRow[] =>
    units
        .filter(isCategorizedBsoUnit)
        .map((unit) => {
            const attributes = getAttributes(unit)
            const property = attributes?.Property?.data?.attributes
            const statuses: string[] = Array.isArray(attributes?.Statuses) ? attributes.Statuses : []
            const currentPrice = toFiniteNumberOrNull(attributes?.Marketing?.Price)
            // No reliable price history is currently loaded here. Use the first known listing price fallback.
            const initialPrice = currentPrice
            const priceDrop = initialPrice != null && currentPrice != null && initialPrice > currentPrice
                ? initialPrice - currentPrice
                : null
            const catalogDate = resolveUnitCatalogDate(unit)
            const marketStatus = resolveMarketStatus(unit)
            const completedOfferEndInfo = marketStatus === 'Off Market' || marketStatus === 'Archived'
                ? resolveCompletedOfferEndInfo(unit, offers)
                : { endDate: null, endReason: null, offerId: null, offerRoute: null }
            const endDate = marketStatus === 'Off Market' || marketStatus === 'Archived' ? completedOfferEndInfo.endDate : null
            const daysPublished = marketStatus === 'Active'
                ? calculateDaysBetween(catalogDate.date, now.toISOString())
                : calculateDaysBetween(catalogDate.date, endDate)

            const unitType = toNonEmptyStringOrNull(attributes?.Type)
            const conditionStatus = resolveConditionStatus(statuses)
            const isDevUnit = isDevEntity(unit)
            const relatedAdvertises = getRelatedItems(unit, advertises, 'Advertises')
            const relatedViewings = getRelatedItems(unit, viewings, 'Viewings')
            const relatedOffers = getRelatedItems(unit, offers, 'Offers')
            const adsCountBreakdown = splitRealTestCounts(relatedAdvertises, isDevUnit)
            const viewingsCountBreakdown = splitRealTestCounts(relatedViewings, isDevUnit)
            const offersCountBreakdown = splitRealTestCounts(relatedOffers, isDevUnit)
            const adsSummary = {
                ...buildAdvertiseSummary(relatedAdvertises, now),
                countBreakdown: adsCountBreakdown,
                manualByBsoCount: relatedAdvertises.filter(isManualBsoAction).length
            }
            const viewingsSummary = {
                ...buildViewingSummary(relatedViewings, now),
                countBreakdown: viewingsCountBreakdown,
                manualByBsoCount: relatedViewings.filter(isManualBsoAction).length
            }
            const offersSummary = {
                ...buildOfferSummary(relatedOffers, now),
                countBreakdown: offersCountBreakdown,
                manualByBsoCount: relatedOffers.filter(isManualBsoAction).length
            }

            return {
                unitId: Number(unit?.id),
                propertyId: toFiniteNumberOrNull(attributes?.Property?.data?.id ?? attributes?.Property?.id),
                isDevUnit,
                headline: String(attributes?.Marketing?.Headline ?? property?.Name ?? getUnitReference(unit)),
                propertyName: String(property?.Name ?? '-'),
                areaName: String(property?.Area ?? property?.Community ?? '-'),
                unitNumber: attributes?.Number != null ? String(attributes.Number) : null,
                erpUnitId: toNonEmptyStringOrNull(attributes?.ERPUnitID),
                unitReference: getUnitReference(unit),
                createdAt: toDateOnly(attributes?.createdAt),
                imageUrl: getUnitCoverImage(unit),
                listingType: resolveListingType(attributes?.Marketing?.Use),
                unitType,
                unitCategory: resolveUnitCategory(attributes, unitType),
                sizeArea: toPositiveNumberOrNull(attributes?.SizeArea),
                roomsLabel: resolveRoomsLabel(attributes),
                marketStatus,
                conditionStatus,
                occupiedUntil: resolveOccupiedUntil(attributes, statuses),
                accessStatuses: resolveAccessStatuses(statuses),
                endDate,
                endReason: marketStatus === 'Off Market' || marketStatus === 'Archived' ? completedOfferEndInfo.endReason : null,
                offerId: completedOfferEndInfo.offerId,
                offerRoute: completedOfferEndInfo.offerRoute,
                daysPublished,
                catalogDate: catalogDate.date,
                catalogDateSource: catalogDate.source,
                initialPrice,
                currentPrice,
                priceDrop,
                initialPriceSource: initialPrice != null ? 'current-price-fallback' : 'unavailable',
                adsCount: adsCountBreakdown.real,
                viewingsCount: viewingsCountBreakdown.real,
                offersCount: offersCountBreakdown.real,
                adsSummary,
                viewingsSummary,
                offersSummary,
                unitReview: normalizeUnitReview(attributes?.UnitReview),
                unitActivity: getUnitReviewActivityEvents(attributes?.UnitReview)
            }
        })

export const buildBsoActivitySummary = (analyticsRows: BsoUnitAnalyticsRow[]): BsoActivitySummary => ({
    advertisements: mergeBreakdownSummaries(analyticsRows.map((row) => row.adsSummary), ADVERTISE_BUCKET_DEFINITIONS),
    viewings: mergeBreakdownSummaries(analyticsRows.map((row) => row.viewingsSummary), VIEWING_BUCKET_DEFINITIONS),
    offers: mergeBreakdownSummaries(analyticsRows.map((row) => row.offersSummary), OFFER_BUCKET_DEFINITIONS)
})

const buildStatisticPeriods = (events: BsoUnitStatisticEvent[], now: Date): StatisticPeriod[] => {
    const currentMonth = getDubaiDateParts(now) ?? getDubaiDateParts(new Date()) as DubaiDateParts
    const previousMonth = shiftMonth(currentMonth, -1)
    const twoMonthsAgo = shiftMonth(currentMonth, -2)
    const previousQuarterMonth = shiftMonth(currentMonth, -3)

    const definitions: Array<{ id: StatisticPeriod['id'], label: string, matches: (event: BsoUnitStatisticEvent) => boolean }> = [
        { id: 'this-month', label: 'This month', matches: (event) => {
            const parts = event.processEndDate != null ? getDubaiDateParts(event.processEndDate) : null
            return parts != null && isSameMonth(parts, currentMonth)
        } },
        { id: 'previous-month', label: 'Previous month', matches: (event) => {
            const parts = event.processEndDate != null ? getDubaiDateParts(event.processEndDate) : null
            return parts != null && isSameMonth(parts, previousMonth)
        } },
        { id: 'two-months-ago', label: '2 months ago', matches: (event) => {
            const parts = event.processEndDate != null ? getDubaiDateParts(event.processEndDate) : null
            return parts != null && isSameMonth(parts, twoMonthsAgo)
        } },
        { id: 'this-quarter', label: 'This quarter', matches: (event) => {
            const parts = event.processEndDate != null ? getDubaiDateParts(event.processEndDate) : null
            return parts != null && isSameQuarter(parts, currentMonth)
        } },
        { id: 'previous-quarter', label: 'Previous quarter', matches: (event) => {
            const parts = event.processEndDate != null ? getDubaiDateParts(event.processEndDate) : null
            return parts != null && isSameQuarter(parts, previousQuarterMonth)
        } },
        { id: 'this-year', label: 'This year', matches: (event) => {
            const parts = event.processEndDate != null ? getDubaiDateParts(event.processEndDate) : null
            return parts != null && isSameYear(parts, currentMonth)
        } },
        { id: 'previous-year', label: 'Previous year', matches: (event) => {
            const parts = event.processEndDate != null ? getDubaiDateParts(event.processEndDate) : null
            return parts != null && isPreviousYear(parts, currentMonth)
        } },
        {
            id: 'last-12-months',
            label: 'Last 12 months',
            matches: (event) => event.processEndDate != null && isWithinLast12Months(event.processEndDate, now)
        }
    ]

    return definitions.map(({ id, label, matches }) => {
        const matchingEvents = events.filter(matches)
        const calculableDays = matchingEvents
            .map((event) => event.daysPublished)
            .filter((days): days is number => days != null)

        return {
            id,
            label,
            count: matchingEvents.length,
            averageDaysPublished: calculableDays.length > 0
                ? Math.round(calculableDays.reduce((total, days) => total + days, 0) / calculableDays.length)
                : null,
            events: matchingEvents
        }
    })
}

export const formatAdminDubaiDate = (value: string | null): string => {
    if (value == null) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: DUBAI_TIME_ZONE,
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    }).format(date)
}

export const buildBsoUnitStatistics = (units: any[], offers: any[], users: any[] = [], now: Date = new Date()): BsoUnitStatistics => {
    const unitsById = new Map<number, any>(units.map((unit) => [Number(unit?.id), unit]))
    const usersById = new Map<number, any>(users.map((user) => [Number(user?.id), user]))
    const bsoUnits = units.filter(isCategorizedBsoUnit)
    const activeBsoUnits = bsoUnits.filter(isActiveBsoUnit)
    const soldBsoUnits = bsoUnits.filter((unit) => unit?.attributes?.Statuses?.includes('Sold'))
    const activeBsoUnitIds = new Set(activeBsoUnits.map((unit) => Number(unit.id)))
    let soldNeedsDataReview = 0

    const soldEvents = offers.reduce<BsoUnitStatisticEvent[]>((events, offer) => {
        const offerAttributes = getAttributes(offer)
        const unitId = Number(offerAttributes?.Unit?.data?.id)
        const unit = unitsById.get(unitId)
        const unitStatuses: string[] = Array.isArray(unit?.attributes?.Statuses) ? unit.attributes.Statuses : []
        const offerStatuses: string[] = Array.isArray(offerAttributes?.Statuses) ? offerAttributes.Statuses : []

        if (unit == null || getUnitSourceCategory(unit) !== 'bso' || !unitStatuses.includes('Sold') || !offerStatuses.includes('Completed')) {
            return events
        }
        const processStartDate = resolveOfferProcessStartDate(offerAttributes)
        const effectiveProcessEndDate = resolveOfferEffectiveProcessEndDate(offerAttributes)
        if (effectiveProcessEndDate.date == null) {
            soldNeedsDataReview += 1
            return events
        }

        events.push({
            unitId,
            unitReference: getUnitReference(unit),
            propertyName: getPropertyName(unit),
            offerId: Number(offer.id),
            offerRoute: buildOfferModalRoute(unitId, Number(offer.id), offerAttributes),
            processStartDate,
            processEndDate: effectiveProcessEndDate.date,
            isProcessEndDateInferred: effectiveProcessEndDate.inferredFromStartDate,
            daysPublished: calculateDaysBetween(resolveUnitStartDate(unit), effectiveProcessEndDate.date),
            trigger: 'Completed Offer'
        })
        return events
    }, [])

    const activeOfferPipeline = offers.reduce<ActiveOfferPipelineItem[]>((items, offer) => {
        const offerAttributes = getAttributes(offer)
        const unitId = Number(offerAttributes?.Unit?.data?.id)
        const statuses: string[] = Array.isArray(offerAttributes?.Statuses) ? offerAttributes.Statuses : []
        const status = statuses[0] ?? 'In-process'

        if (!activeBsoUnitIds.has(unitId) || TERMINAL_OFFER_STATUSES.has(status)) return items
        const unit = unitsById.get(unitId)
        const agentId = Number(offerAttributes?.User?.data?.id)
        const agent = usersById.get(agentId)
        items.push({
            unitId,
            unitReference: getUnitReference(unit),
            propertyName: getPropertyName(unit),
            offerId: Number(offer.id),
            offerRoute: buildOfferModalRoute(unitId, Number(offer.id), offerAttributes),
            unitCoverImage: getUnitCoverImage(unit),
            status,
            processStartDate: resolveOfferProcessStartDate(offerAttributes),
            processEndDate: resolveOfferProcessEndDate(offerAttributes),
            agentName: resolveAgentName(agent),
            agentCompany: agent?.Settings?.[0]?.Company ?? null,
            agentBirthYear: resolveAgentBirthYear(agent),
            agentRoute: buildAgentRoute(agentId)
        })
        return items
    }, [])

    const soldPeriods = buildStatisticPeriods(soldEvents, now)
    const soldEventByUnitId = soldEvents.reduce<Map<number, BsoUnitStatisticEvent>>((eventsByUnit, event) => {
        const current = eventsByUnit.get(event.unitId)
        if (current == null || new Date(event.processEndDate ?? 0).getTime() > new Date(current.processEndDate ?? 0).getTime()) {
            eventsByUnit.set(event.unitId, event)
        }
        return eventsByUnit
    }, new Map())
    const buildStatisticsUnitDetail = (unit: any): BsoStatisticsUnitDetailItem => {
        const detail = buildUnitDetailItem(unit)
        const soldEvent = soldEventByUnitId.get(detail.unitId)
        if (soldEvent == null) return detail

        return {
            ...detail,
            offerId: soldEvent.offerId,
            offerRoute: soldEvent.offerRoute,
            processStartDate: soldEvent.processStartDate,
            processEndDate: soldEvent.processEndDate,
            endDate: soldEvent.processEndDate,
            endReason: `Completed offer #${String(soldEvent.offerId)}`,
            isProcessEndDateInferred: soldEvent.isProcessEndDateInferred,
            daysPublished: soldEvent.daysPublished
        }
    }

    return {
        totalUnits: bsoUnits.length,
        totalRentalUnits: bsoUnits.filter((unit) => unit?.attributes?.Marketing?.Use === 'For rent').length,
        totalSaleUnits: bsoUnits.filter((unit) => unit?.attributes?.Marketing?.Use === 'For sale').length,
        activeRentals: activeBsoUnits.filter((unit) => unit?.attributes?.Marketing?.Use === 'For rent').length,
        activeSales: activeBsoUnits.filter((unit) => unit?.attributes?.Marketing?.Use === 'For sale').length,
        activeTotal: activeBsoUnits.length,
        soldUnits: soldBsoUnits.length,
        soldRentUnits: soldBsoUnits.filter((unit) => unit?.attributes?.Marketing?.Use === 'For rent').length,
        soldSaleUnits: soldBsoUnits.filter((unit) => unit?.attributes?.Marketing?.Use === 'For sale').length,
        soldPeriods,
        soldThisMonth: soldPeriods.find((period) => period.id === 'this-month')?.count ?? 0,
        soldNeedsDataReview,
        activeOfferPipeline,
        totalUnitDetails: bsoUnits.map(buildStatisticsUnitDetail),
        activeUnitDetails: activeBsoUnits.map(buildStatisticsUnitDetail),
        soldUnitDetails: soldBsoUnits.map(buildStatisticsUnitDetail)
    }
}
